import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'components'
import UnvDrawer from 'components/UnvDialog/UnvDrawer'
import UnvModal from 'components/UnvDialog/UnvModal'
import HistoryViewer from 'components/HistoryViewer/HistoryViewer'
import {
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  DataTable,
  UnvButton,
} from 'components'
import {
  listStudents,
  getStudentCard,
  transferGroup,
  changeStatus,
} from 'service/StudentsService'
import styles from '../AdminFullPage.module.scss'

const PAGE_SIZE = 20

function StudentsTab({ notify }) {
  const { t } = useTranslation('common')

  const [items, setItems] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({
    q: '',
    status: '',
    course: '',
    group: '',
    form: '',
    funding: '',
  })
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const [drawer, setDrawer] = useState(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerTab, setDrawerTab] = useState('main')

  const [transferOpen, setTransferOpen] = useState(false)
  const [transferData, setTransferData] = useState({ group: '', reason: '' })
  const [statusOpen, setStatusOpen] = useState(false)
  const [statusData, setStatusData] = useState({ status: '', reason: '' })

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(false)
    const params = { page, page_size: PAGE_SIZE }
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params[k] = v
    })
    listStudents(params)
      .then((res) => {
        const data = res?.data || res
        setItems(Array.isArray(data) ? data : data?.results || [])
        setCount(data?.count ?? (Array.isArray(data) ? data.length : 0))
      })
      .catch(() => {
        setLoadError(true)
        setItems([])
        setCount(0)
      })
      .finally(() => setLoading(false))
  }, [page, filters])

  useEffect(() => {
    load()
  }, [load])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count || 0) / PAGE_SIZE)),
    [count],
  )

  const openCard = (id) => {
    setDrawerLoading(true)
    setDrawerTab('main')
    getStudentCard(id)
      .then((res) => setDrawer(res?.data || res))
      .catch(() => setDrawer(null))
      .finally(() => setDrawerLoading(false))
  }

  const refreshCard = () => {
    if (!drawer?.id) return
    getStudentCard(drawer.id)
      .then((res) => setDrawer(res?.data || res))
      .catch(() => {})
  }

  const onTransfer = () => {
    if (!drawer?.id) return
    transferGroup(drawer.id, transferData)
      .then(() => {
        notify('Перевод выполнен', 'success')
        setTransferOpen(false)
        refreshCard()
      })
      .catch(() => notify('Не удалось перевести', 'error'))
  }

  const onChangeStatus = () => {
    if (!drawer?.id) return
    changeStatus(drawer.id, statusData)
      .then(() => {
        notify('Статус изменён', 'success')
        setStatusOpen(false)
        refreshCard()
      })
      .catch(() => notify('Не удалось сменить статус', 'error'))
  }

  return (
    <div>
      <h2 className={styles.section_title}>{t('menu.students')}</h2>

      <div className={styles.toolbar}>
        <input
          className="unv-input"
          placeholder={t('pages.studentsTab.search_placeholder')}
          value={filters.q}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, q: e.target.value }))
          }}
        />
        <select
          className="unv-select"
          value={filters.status}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, status: e.target.value }))
          }}
        >
          <option value="">{t('pages.ordersPage.all_statuses')}</option>
          <option value="active">{t('pages.studentsTab.status_active')}</option>
          <option value="academic_leave">{t('pages.studentsTab.status_acad_leave')}</option>
          <option value="expelled">{t('pages.studentsTab.status_expelled')}</option>
          <option value="graduated">{t('pages.studentsTab.status_graduated')}</option>
        </select>
        <select
          className="unv-select"
          value={filters.course}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, course: e.target.value }))
          }}
        >
          <option value="">{t('pages.studentsTab.all_courses')}</option>
          {[1, 2, 3, 4, 5, 6].map((c) => (
            <option key={c} value={c}>
              {c} курс
            </option>
          ))}
        </select>
        <input
          className="unv-input"
          placeholder={t('schedule.group')}
          value={filters.group}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, group: e.target.value }))
          }}
        />
        <select
          className="unv-select"
          value={filters.form}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, form: e.target.value }))
          }}
        >
          <option value="">{t('pages.studentsTab.all_forms')}</option>
          <option value="full_time">{t('archive.form_full_time')}</option>
          <option value="part_time">{t('archive.form_part_time')}</option>
          <option value="evening">{t('archive.form_evening')}</option>
        </select>
        <select
          className="unv-select"
          value={filters.funding}
          onChange={(e) => {
            setPage(1)
            setFilters((s) => ({ ...s, funding: e.target.value }))
          }}
        >
          <option value="">{t('pages.studentsTab.all_funding')}</option>
          <option value="budget">{t('pages.enrollTab.funding_budget')}</option>
          <option value="contract">{t('pages.enrollTab.funding_contract')}</option>
        </select>
      </div>

      {loading ? (
        <Skeleton variant="table" rows={8} cols={7} />
      ) : loadError ? (
        <ErrorState title={t('pages.studentsTab.not_found')} onRetry={load} />
      ) : items.length === 0 ? (
        <EmptyState iconName="users" title={t('pages.studentsTab.not_found')} />
      ) : (
        <DataTable
          columns={[
            { key: 'id', label: 'ID', sortable: true, width: 80 },
            {
              key: 'full_name',
              label: t('common.fullName'),
              sortable: true,
              render: (_, s) => s.full_name || [s.surname, s.first_name, s.last_name].filter(Boolean).join(' '),
            },
            { key: 'inn', label: t('auth.inn'), render: (v) => v || '—' },
            { key: 'group', label: t('schedule.group'), render: (_, s) => s.group?.name || s.group || '—' },
            { key: 'course', label: t('dpo.course'), sortable: true, render: (v) => v || '—' },
            { key: 'form', label: t('studentDash.form_label'), render: (v) => v || '—' },
            {
              key: 'status',
              label: t('common.status'),
              render: (v) => {
                const variant =
                  v === 'active' ? 'success'
                    : v === 'academic_leave' ? 'warning'
                      : v === 'expelled' ? 'danger'
                        : v === 'graduated' ? 'info'
                          : 'muted'
                return <Badge variant={variant} dot>{v || '—'}</Badge>
              },
            },
          ]}
          data={items}
          pageSize={PAGE_SIZE}
          onRowClick={(row) => openCard(row.id)}
          searchable={false}
          emptyText={t('pages.studentsTab.not_found')}
        />
      )}

      <div className="unv-pagination">
        <span>
          Всего: {count} • Стр. {page} из {totalPages}
        </span>
        <div className="unv-pagination__controls">
          <button
            type="button"
            className="unv-btn unv-btn--ghost unv-btn--sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>
          <button
            type="button"
            className="unv-btn unv-btn--ghost unv-btn--sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперёд
          </button>
        </div>
      </div>

      <UnvDrawer
        open={!!drawer}
        title={
          drawer
            ? drawer.full_name ||
              [drawer.surname, drawer.first_name].filter(Boolean).join(' ')
            : ''
        }
        onClose={() => setDrawer(null)}
      >
        {drawerLoading ? (
          <div className={styles.spinner_box}>
            <Spinner size={28} />
          </div>
        ) : drawer ? (
          <div>
            <div className="unv-tabs">
              {['main', 'documents', 'parents', 'groups', 'statuses', 'history'].map(
                (key) => (
                  <button
                    key={key}
                    type="button"
                    className={`unv-tab ${drawerTab === key ? 'unv-tab--active' : ''}`}
                    onClick={() => setDrawerTab(key)}
                  >
                    {key === 'main'
                      ? 'Основное'
                      : key === 'documents'
                        ? 'Документы'
                        : key === 'parents'
                          ? 'Родители'
                          : key === 'groups'
                            ? 'История групп'
                            : key === 'statuses'
                              ? 'История статусов'
                              : 'История'}
                  </button>
                ),
              )}
            </div>

            {drawerTab === 'main' ? (
              <>
                <div className={styles.kv}>
                  <div className={styles.kv_key}>ID</div>
                  <div>{drawer.id}</div>
                  <div className={styles.kv_key}>{t('common.fullName')}</div>
                  <div>
                    {[drawer.surname, drawer.first_name, drawer.last_name]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                  <div className={styles.kv_key}>{t('auth.inn')}</div>
                  <div>{drawer.inn || '—'}</div>
                  <div className={styles.kv_key}>{t('schedule.group')}</div>
                  <div>{drawer.group?.name || drawer.group || '—'}</div>
                  <div className={styles.kv_key}>{t('dpo.course')}</div>
                  <div>{drawer.course || '—'}</div>
                  <div className={styles.kv_key}>{t('studentDash.form_label')}</div>
                  <div>{drawer.form || '—'}</div>
                  <div className={styles.kv_key}>{t('pages.enrollTab.funding')}</div>
                  <div>{drawer.funding || '—'}</div>
                  <div className={styles.kv_key}>{t('common.status')}</div>
                  <div>
                    {drawer.status ? (
                      <Badge
                        variant={
                          drawer.status === 'active' ? 'success'
                            : drawer.status === 'academic_leave' ? 'warning'
                              : drawer.status === 'expelled' ? 'danger'
                                : drawer.status === 'graduated' ? 'info'
                                  : 'muted'
                        }
                        dot
                      >
                        {drawer.status}
                      </Badge>
                    ) : '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <UnvButton
                    variant="primary"
                    iconLeft="users"
                    onClick={() => {
                      setTransferData({ group: '', reason: '' })
                      setTransferOpen(true)
                    }}
                  >
                    Перевод в группу
                  </UnvButton>
                  <UnvButton
                    variant="ghost"
                    iconLeft="edit"
                    onClick={() => {
                      setStatusData({ status: '', reason: '' })
                      setStatusOpen(true)
                    }}
                  >
                    Сменить статус
                  </UnvButton>
                </div>
              </>
            ) : null}

            {drawerTab === 'documents' ? (
              <>
                {Array.isArray(drawer.documents) && drawer.documents.length > 0 ? (
                  <table className="unv-table">
                    <thead>
                      <tr>
                        <th>{t('schedule.type')}</th>
                        <th>{t('common.name')}</th>
                        <th>{t('common.date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.documents.map((d) => (
                        <tr key={d.id}>
                          <td>{d.type || '—'}</td>
                          <td>{d.title || d.name || '—'}</td>
                          <td>{d.created_at || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EmptyState iconName="folder" title={t('pages.studentsTab.no_documents')} variant="compact" />
                )}
              </>
            ) : null}

            {drawerTab === 'parents' ? (
              <>
                {Array.isArray(drawer.parents) && drawer.parents.length > 0 ? (
                  <table className="unv-table">
                    <thead>
                      <tr>
                        <th>{t('common.fullName')}</th>
                        <th>{t('schedule.type')}</th>
                        <th>{t('common.phone')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.parents.map((p) => (
                        <tr key={p.id || p.phone}>
                          <td>{p.full_name || p.name || '—'}</td>
                          <td>{p.type || '—'}</td>
                          <td>{p.phone || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EmptyState iconName="users" title={t('pages.studentsTab.no_parents')} variant="compact" />
                )}
              </>
            ) : null}

            {drawerTab === 'groups' ? (
              <>
                {Array.isArray(drawer.group_history) &&
                drawer.group_history.length > 0 ? (
                  <table className="unv-table">
                    <thead>
                      <tr>
                        <th>{t('schedule.group')}</th>
                        <th>С</th>
                        <th>{t('pages.studentsTab.to_th')}</th>
                        <th>{t('schedule.reason_col')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.group_history.map((g, i) => (
                        <tr key={g.id || i}>
                          <td>{g.group?.name || g.group || '—'}</td>
                          <td>{g.from_date || '—'}</td>
                          <td>{g.to_date || '—'}</td>
                          <td>{g.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EmptyState iconName="folder" title={t('pages.studentsTab.no_group_history')} variant="compact" />
                )}
              </>
            ) : null}

            {drawerTab === 'statuses' ? (
              <>
                {Array.isArray(drawer.status_history) &&
                drawer.status_history.length > 0 ? (
                  <table className="unv-table">
                    <thead>
                      <tr>
                        <th>{t('common.status')}</th>
                        <th>{t('pages.dashboardTab.col_when')}</th>
                        <th>{t('schedule.reason_col')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.status_history.map((g, i) => (
                        <tr key={g.id || i}>
                          <td>{g.status || '—'}</td>
                          <td>{g.created_at || g.date || '—'}</td>
                          <td>{g.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EmptyState iconName="folder" title={t('pages.studentsTab.no_status_history')} variant="compact" />
                )}
              </>
            ) : null}

            {drawerTab === 'history' ? (
              <HistoryViewer
                module="students"
                targetId={drawer.id}
                targetType="student"
                title={t('pages.studentsTab.audit_student')}
              />
            ) : null}
          </div>
        ) : null}
      </UnvDrawer>

      <UnvModal
        open={transferOpen}
        title={t('pages.studentsTab.transfer_group')}
        onClose={() => setTransferOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="unv-btn unv-btn--ghost"
              onClick={() => setTransferOpen(false)}
            >
              Отмена
            </button>
            <button type="button" className="unv-btn" onClick={onTransfer}>
              {t('statements.info.transfer')}
            </button>
          </>
        }
      >
        <label className="unv-label">{t('pages.studentsTab.new_group')}</label>
        <input
          className="unv-input"
          value={transferData.group}
          onChange={(e) =>
            setTransferData((s) => ({ ...s, group: e.target.value }))
          }
        />
        <div style={{ marginTop: 12 }}>
          <label className="unv-label">{t('schedule.reason_col')}</label>
          <textarea
            className="unv-textarea"
            value={transferData.reason}
            onChange={(e) =>
              setTransferData((s) => ({ ...s, reason: e.target.value }))
            }
          />
        </div>
      </UnvModal>

      <UnvModal
        open={statusOpen}
        title={t('pages.studentsTab.status_change')}
        onClose={() => setStatusOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="unv-btn unv-btn--ghost"
              onClick={() => setStatusOpen(false)}
            >
              Отмена
            </button>
            <button type="button" className="unv-btn" onClick={onChangeStatus}>
              {t('pages.studentsTab.change_btn')}
            </button>
          </>
        }
      >
        <label className="unv-label">{t('pages.studentsTab.new_status')}</label>
        <select
          className="unv-select"
          value={statusData.status}
          onChange={(e) =>
            setStatusData((s) => ({ ...s, status: e.target.value }))
          }
        >
          <option value="">—</option>
          <option value="active">{t('pages.studentsTab.status_active')}</option>
          <option value="academic_leave">{t('pages.studentsTab.status_acad_leave')}</option>
          <option value="expelled">{t('pages.studentsTab.status_expelled')}</option>
          <option value="graduated">{t('pages.studentsTab.status_graduated')}</option>
        </select>
        <div style={{ marginTop: 12 }}>
          <label className="unv-label">{t('schedule.reason_col')}</label>
          <textarea
            className="unv-textarea"
            value={statusData.reason}
            onChange={(e) =>
              setStatusData((s) => ({ ...s, reason: e.target.value }))
            }
          />
        </div>
      </UnvModal>
    </div>
  )
}

export default StudentsTab
